import { useEffect, useMemo, useRef } from "react";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import type { ProjectManifest } from "@pix/shared";
import { pixLanguage } from "../lang/pixLanguage";
import { pixCompletionSource } from "../lang/pixCompletion";
import { EditorView } from "@codemirror/view";
import { autocompletion } from "@codemirror/autocomplete";

interface Props {
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  filename?: string;
  /** 指定行へジャンプ(1-based) */
  scrollToLine?: number | null;
  /** 完了通知。親側で null にリセットする想定 */
  onScrolled?: () => void;
  /** manifest(補完用) */
  manifest?: ProjectManifest;
}

export function ScriptEditor({
  value,
  onChange,
  readOnly = false,
  filename,
  scrollToLine,
  onScrolled,
  manifest,
}: Props) {
  const ref = useRef<ReactCodeMirrorRef>(null);

  // 補完は manifest を参照するので ref で常時最新を見るようにする
  const manifestRef = useRef<ProjectManifest | undefined>(manifest);
  manifestRef.current = manifest;

  const extensions = useMemo(
    () => [
      pixLanguage,
      autocompletion({
        override: [
          pixCompletionSource({
            manifestRef: manifestRef as { current: ProjectManifest },
          }),
        ],
        activateOnTyping: true,
        closeOnBlur: true,
      }),
      EditorView.lineWrapping,
      EditorView.theme({
        "&": {
          height: "100%",
          fontSize: "14px",
        },
        ".cm-scroller": {
          fontFamily:
            '"Cascadia Code", "Fira Code", "Yu Gothic UI", "Hiragino Sans", monospace',
          lineHeight: "1.6",
        },
        ".cm-content": { padding: "12px 8px" },
        ".cm-gutters": {
          backgroundColor: "#0d1119",
          color: "#445566",
          border: "none",
        },
      }),
    ],
    [],
  );

  useEffect(() => {
    if (ref.current?.view && filename) {
      // ファイル切り替え時に先頭にスクロール
      ref.current.view.dispatch({
        selection: { anchor: 0 },
        scrollIntoView: true,
      });
    }
  }, [filename]);

  useEffect(() => {
    if (scrollToLine == null) return;
    const view = ref.current?.view;
    if (!view) return;
    try {
      const lineInfo = view.state.doc.line(
        Math.max(1, Math.min(scrollToLine, view.state.doc.lines)),
      );
      view.dispatch({
        selection: { anchor: lineInfo.from },
        scrollIntoView: true,
        effects: EditorView.scrollIntoView(lineInfo.from, { y: "center" }),
      });
    } catch {
      /* line 範囲外は無視 */
    }
    onScrolled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToLine]);

  return (
    <CodeMirror
      ref={ref}
      value={value}
      onChange={onChange}
      extensions={extensions}
      readOnly={readOnly}
      theme="dark"
      basicSetup={{
        lineNumbers: true,
        highlightActiveLine: true,
        foldGutter: false,
        autocompletion: false, // 自前の補完を使うので basicSetup の方は無効化
        bracketMatching: true,
      }}
      style={{ height: "100%" }}
    />
  );
}
